import {
  Button,
  Checkbox,
  Fab,
  FormControlLabel,
  makeStyles,
} from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { format } from "date-fns";
import {
  getDateFormats,
  getDateTimeLocale,
  getInputDateFormats,
} from "../../../../../utils/get-date-time-locale";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMembersByName } from "../../../../../services/formCombos.service.js";
import {
  addApprovals,
  getApprovalsByCommitteeSlotId,
  removeVote,
} from "../../../../../services/slotApprovals.service";
import ApproverCard from "../../../../Common/ApproverCard";
import { Select } from "../../../../Common/Select";
import Textarea from "../../../../Common/Textarea";
import Toastmessage from "../../../../Common/ToastMessage";
import { ConcludeButtonContainer, Header, VotingContainer } from "./styles";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
  },
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minWidth: 180,
    minHeight: 50,
    borderRadius: 25,
    marginTop: "5vh",
  },
  spacer: {
    marginBottom: 20,
  },
  success: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: 5,
    height: 50,
  },
  label: {
    ...theme.typography.subtitle1,
    color: theme.palette.primary.contrastText,
  },
  icon: {
    color: theme.palette.primary.contrastText,
    fontSize: 30,
    marginRight: 50,
  },
  titleModal: {
    color: "#031795",
    fontWeight: "bold",
    fontSize: "20px",
  },
}));

const SlotApproval = ({
  slotToApproval,
  minuteStatus,
  t,
  actionShowSpinner,
}) => {
  const classes = useStyles();
  const { i18n } = useTranslation();

  const useEffectAlreadyRun = useRef(false);

  const [toastMessage, setToastMessage] = useState({
    message: "",
    type: "error",
    open: false,
  });
  const [canSave, setCanSave] = useState(false);

  const [approvedInputValue, setApprovedInputValue] = useState("");
  const [approvedSelectValue, setApprovedSelectValue] = useState();
  const [unanimityApproval, setUnanimityApproval] = useState(false);
  const [approvedSelectOptions, setApprovedSelectOptions] = useState([]);

  const [reprovedInputValue, setReprovedInputValue] = useState("");
  const [reprovedSelectValue, setReprovedSelectValue] = useState();
  const [unanimityDisapproval, setUnanimityDisapproval] = useState(false);
  const [reprovedSelectOptions, setReprovedSelectOptions] = useState([]);

  const [caveat, setCaveat] = useState("");
  const [approvedCaveat, setApprovedCaveat] = useState("");
  const [reprovedCaveat, setReprovedCaveat] = useState("");
  const [roundRobinApprovedCaveat, setRoundRobinApprovedCaveat] = useState("");

  const [roudRobbinApprovedInputValue, setRoudRobbinApprovedInputValue] =
    useState("");
  const [roudRobbinApprovedSelectValue, setRoudRobbinApprovedSelectValue] =
    useState();
  const [roudRobbinApprovedSelectOptions, setRoudRobbinApprovedSelectOptions] =
    useState([]);
  const [roudRobbindate, setRoudRobbindate] = useState(new Date());

  const [approvedVotes, setApprovedVotes] = useState([]);
  const [reprovedVotes, setReprovedVotes] = useState([]);
  const [caveats, setCaveats] = useState([]);
  const [roundRobinVotes, setRoundRobinVotes] = useState([]);

  const fetchApprovals = async () => {
    actionShowSpinner(true);

    const response = await getApprovalsByCommitteeSlotId({
      committeeSlotId: slotToApproval.comitteeSlotId,
    });

    setUnanimityApproval(response.unanimityApproved);
    setUnanimityDisapproval(response.unanimityDisapproved);

    const approvedVotes = response.approvers
      .filter((approval) => approval.vote === "A" || approval.vote === "AR")
      .map((approvedVote) => ({
        id: approvedVote.memberId,
        voteId: approvedVote.id,
        caveat: approvedVote.resalvas,
        name: approvedVote.memberName,
        isPersisted: true,
      }));

    setApprovedVotes(approvedVotes);

    const reprovedVotes = response.approvers
      .filter((approval) => approval.vote === "R")
      .map((reprovedVote) => ({
        id: reprovedVote.memberId,
        voteId: reprovedVote.id,
        caveat: reprovedVote.resalvas,
        name: reprovedVote.memberName,
        isPersisted: true,
      }));

    setReprovedVotes(reprovedVotes);

    const caveats = response.approvers
      .filter((approval) => approval.vote === "C")
      .map((caveat) => ({
        voteId: caveat.id,
        isPersisted: true,
        caveat: caveat.resalvas,
      }));

    setCaveats(caveats);

    const roudRobbinApprovedVotes = response.approvers
      .filter((approval) => approval.vote === "RR")
      .map((caveatApprovedVotes) => ({
        id: caveatApprovedVotes.memberId,
        voteId: caveatApprovedVotes.id,
        caveat: caveatApprovedVotes.resalvas,
        name: caveatApprovedVotes.memberName,
        isPersisted: true,
        roudRobbinDate: caveatApprovedVotes.roudRobbinDate,
        formattedRoudRobbinDate: format(
          new Date(caveatApprovedVotes.roudRobbinDate),
          getDateFormats(i18n.language),
          { locale: getDateTimeLocale(i18n.language) }
        ),
      }));

    setRoundRobinVotes(roudRobbinApprovedVotes);
    actionShowSpinner(false);

    useEffectAlreadyRun.current = true;
  };

  useEffect(() => {
    async function loadApproversSelectOptions() {
      if (approvedInputValue?.length >= 3) {
        const res = await getMembersByName("pt", approvedInputValue);

        if (res.error?.length > 0) {
          setApprovedSelectOptions([]);
        }

        const approvedIdVoters = approvedVotes?.map((vote) => vote.id);
        const reprovedIdVoters = reprovedVotes?.map((vote) => vote.id);
        const roudRobbinIdVoters = roundRobinVotes?.map((vote) => vote.id);

        const voters = [
          ...approvedIdVoters,
          ...reprovedIdVoters,
          ...roudRobbinIdVoters,
        ];

        const list = res.data.filter((member) => !voters.includes(member.id));

        setApprovedSelectOptions(list);
      } else {
        setApprovedSelectOptions([]);
      }
    }

    async function loadReprovedSelectOptions() {
      if (reprovedInputValue?.length >= 3) {
        const res = await getMembersByName("pt", reprovedInputValue);

        if (res.error?.length > 0) {
          setReprovedSelectOptions([]);
        }

        const approvedIdVoters = approvedVotes?.map((vote) => vote.id);
        const reprovedIdVoters = reprovedVotes?.map((vote) => vote.id);
        const roudRobbinIdVoters = roundRobinVotes?.map((vote) => vote.id);

        const voters = [
          ...approvedIdVoters,
          ...reprovedIdVoters,
          ...roudRobbinIdVoters,
        ];

        const list = res.data.filter((member) => !voters.includes(member.id));

        setReprovedSelectOptions(list);
      } else {
        setReprovedSelectOptions([]);
      }
    }

    async function loadRoudRobbinApprovedOptions() {
      if (roudRobbinApprovedInputValue?.length >= 3) {
        const res = await getMembersByName("pt", roudRobbinApprovedInputValue);

        if (res.error?.length > 0) {
          setRoudRobbinApprovedSelectOptions([]);
        }

        const approvedIdVoters = approvedVotes?.map((vote) => vote.id);
        const reprovedIdVoters = reprovedVotes?.map((vote) => vote.id);
        const roudRobbinIdVoters = roundRobinVotes?.map((vote) => vote.id);

        const voters = [
          ...approvedIdVoters,
          ...reprovedIdVoters,
          ...roudRobbinIdVoters,
        ];

        const list = res.data.filter((member) => !voters.includes(member.id));

        setRoudRobbinApprovedSelectOptions(list);
      } else {
        setRoudRobbinApprovedSelectOptions([]);
      }
    }

    loadApproversSelectOptions();
    loadReprovedSelectOptions();
    loadRoudRobbinApprovedOptions();
  }, [
    approvedInputValue,
    reprovedInputValue,
    roudRobbinApprovedInputValue,
    caveats,
    reprovedVotes,
    approvedVotes,
  ]);

  useEffect(() => {
    if (!useEffectAlreadyRun.current) {
      fetchApprovals();
    }

    if (minuteStatus !== "C" && minuteStatus !== "NI") {
      setCanSave(true);

      return;
    }
  }, [minuteStatus]);

  const handleAddApprovedVote = () => {
    if (!approvedSelectValue) {
      setToastMessage({
        message: t("forms.minutes.actions.votesValidatioError"),
        type: "error",
        open: true,
      });

      return;
    }

    setApprovedVotes([
      ...approvedVotes,
      {
        ...approvedSelectValue,
        ...(approvedCaveat.length > 0 && {
          caveat: approvedCaveat,
        }),
        isPersisted: false,
      },
    ]);
    setApprovedSelectValue();
    setApprovedCaveat("");
  };

  const handleRemoveApprovedVote = (
    memberToRemove,
    isPersisted,
    approverName
  ) => {
    if (isPersisted) {
      handleDeletePersistedVote(memberToRemove);
    }

    if (!memberToRemove) {
      setApprovedVotes(
        approvedVotes.filter((vote) => vote.name !== approverName)
      );
    } else {
      setApprovedVotes(
        approvedVotes.filter((vote) => vote.voteId !== memberToRemove)
      );
    }
  };

  const handleAddReprovedVote = () => {
    if (!reprovedSelectValue) {
      setToastMessage({
        message: t("forms.minutes.actions.votesValidatioError"),
        type: "error",
        open: true,
      });

      return;
    }

    setReprovedVotes([
      ...reprovedVotes,
      {
        ...reprovedSelectValue,
        ...(reprovedCaveat.length > 0 && {
          caveat: reprovedCaveat,
        }),
        isPersisted: false,
      },
    ]);
    setReprovedSelectValue();
    setReprovedCaveat("");
  };

  const handleRemoveReprovedVote = (
    memberToRemove,
    isPersisted,
    participantName
  ) => {
    if (isPersisted) {
      handleDeletePersistedVote(memberToRemove);
    }

    if (!memberToRemove) {
      setReprovedVotes(
        reprovedVotes.filter((vote) => vote.name !== participantName)
      );
    } else {
      setReprovedVotes(
        reprovedVotes.filter((vote) => vote.voteId !== memberToRemove)
      );
    }
  };

  const handleRemoveCaveat = ({ caveatId, isPersisted, caveatDescription }) => {
    if (isPersisted) {
      handleDeletePersistedVote(caveatId);
    }

    if (!caveatId) {
      setCaveats(caveats.filter((vote) => vote.caveat !== caveatDescription));
    } else {
      setCaveats(caveats.filter((vote) => vote.voteId !== caveatId));
    }
  };

  const handleAddCaveats = () => {
    if (!caveat) {
      setToastMessage({
        message: t("forms.minutes.actions.votesValidatioError"),
        type: "error",
        open: true,
      });

      return;
    }

    setCaveats([...caveats, { caveat, isPersisted: false }]);
    setCaveat("");
  };

  const handleRemoveRoudRobbinApprovedVote = ({
    memberToRemove,
    isPersisted,
    roundRobinVoterName,
  }) => {
    if (isPersisted) {
      handleDeletePersistedVote(memberToRemove);
    }

    if (!memberToRemove) {
      setRoundRobinVotes(
        roundRobinVotes.filter((vote) => vote.name !== roundRobinVoterName)
      );
    } else {
      setRoundRobinVotes(
        roundRobinVotes.filter((vote) => vote.voteId !== memberToRemove)
      );
    }
  };

  const handleAddRoudRobbinApprovedVote = () => {
    if (!roudRobbindate || !roudRobbinApprovedSelectValue) {
      setToastMessage({
        message: t("forms.minutes.actions.votesValidatioError"),
        type: "error",
        open: true,
      });

      return;
    }

    setRoundRobinVotes([
      ...roundRobinVotes,
      {
        ...roudRobbinApprovedSelectValue,
        ...(roundRobinApprovedCaveat.length > 0 && {
          caveat: roundRobinApprovedCaveat,
        }),
        roudRobbindate,
        formattedRoudRobbinDate: format(
          new Date(roudRobbindate),
          getDateFormats(i18n.language),
          { locale: getDateTimeLocale(i18n.language) }
        ),
        isPersisted: false,
      },
    ]);
    setRoudRobbindate(new Date());
    setRoudRobbinApprovedSelectValue();
    setRoundRobinApprovedCaveat("");
  };

  const handleSave = async () => {
    actionShowSpinner(true);

    const response = await addApprovals({
      committeeSlotId: slotToApproval.comitteeSlotId,
      approvers: approvedVotes
        .filter((vote) => !vote.isPersisted)
        .map((voter) => ({ idMember: voter.id, caveat: voter.caveat })),
      repprovers: reprovedVotes
        .filter((vote) => !vote.isPersisted)
        .map((voter) => ({ idMember: voter.id, caveat: voter.caveat })),
      caveats: caveats
        .filter((vote) => !vote.isPersisted)
        .map((voter) => ({
          caveat: voter.caveat,
        })),
      approvedRoudRobbins: roundRobinVotes
        .filter((vote) => !vote.isPersisted)
        .map((voter) => ({
          idMember: voter.id,
          roudRobbinDate: voter.roudRobbindate,
          caveat: voter.caveat,
        })),
      unanimityApproval,
      unanimityDisapproval,
    });

    await fetchApprovals();

    if (response?.error) {
      setToastMessage({
        message: t("menu.minutes.slotApproval.saveError"),
        type: "error",
        open: false,
      });

      return;
    }

    actionShowSpinner(false);
  };

  const updateDisapprovalUnannimity = useCallback(() => {
    setUnanimityDisapproval(!unanimityDisapproval);

    setApprovedVotes([]);
    setReprovedVotes([]);
    setRoundRobinVotes([]);
  }, [unanimityDisapproval]);

  const updateApprovalUnanimity = useCallback(() => {
    setUnanimityApproval(!unanimityApproval);

    setApprovedVotes([]);
    setReprovedVotes([]);
    setRoundRobinVotes([]);
  }, [unanimityApproval]);

  const handleDeletePersistedVote = useCallback(async (voteId) => {
    actionShowSpinner(true);

    const response = await removeVote({ id: voteId });

    actionShowSpinner(false);

    if (response?.error) {
      setToastMessage({
        open: false,
        content: t("menu.minutes.slotApproval.saveError"),
        type: "error",
        autoHideDuration: 4000,
      });
    }
  }, []);

  return (
    <>
      <Toastmessage
        message={toastMessage.message}
        type={toastMessage.type}
        open={toastMessage.open}
        autoHideDuration={toastMessage.autoHideDuration}
        close={() =>
          setToastMessage({
            open: false,
            content: "",
            type: "",
            autoHideDuration: 4000,
          })
        }
      />
      <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
        <Header>
          <span className={classes.titleModal}>VOTOS</span>
        </Header>

        <VotingContainer>
          <div className="selection-side">
            <span style={{ color: "#66b246" }}>
              {t("forms.minutes.actions.approved").toUpperCase()}
            </span>

            <div className="selection-container">
              <div className="inputs-container">
                <Select
                  disabled={!canSave}
                  id="participants"
                  options={approvedSelectOptions}
                  value={approvedSelectValue}
                  setValue={(value) => {
                    setApprovedSelectValue(value);
                    setApprovedSelectOptions([]);
                  }}
                  onInputChange={(_event, newInputValue, _prop) => {
                    setApprovedInputValue(newInputValue);
                  }}
                  inputValue={approvedInputValue}
                  label={t("name")}
                />

                <Textarea
                  name="ApprovedCaveats"
                  id="ApprovedCaveats"
                  value={approvedCaveat}
                  disabled={!canSave}
                  label={t("forms.minutes.actions.caveats")}
                  onChange={(e) => setApprovedCaveat(e.target.value)}
                />
              </div>
              <div className="add-button-container">
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  onClick={handleAddApprovedVote}
                  disabled={unanimityApproval | unanimityDisapproval | !canSave}
                >
                  <Add />
                </Fab>
              </div>
            </div>

            <FormControlLabel
              fullWidth
              control={
                <Checkbox
                  value={unanimityApproval}
                  checked={unanimityApproval}
                  disabled={unanimityDisapproval | !canSave}
                  onChange={() => updateApprovalUnanimity()}
                  name="unanimityApproval"
                  id="unanimityApproval"
                  color="primary"
                />
              }
              label={t("menu.minutes.slotApproval.unanimityApproval")}
            />
          </div>
          <div className="voters-side">
            {approvedVotes.map((approvedVote, index) => (
              <ApproverCard
                key={approvedVote.voteId}
                approver={{
                  memberName: approvedVote.name,
                  approvalId: approvedVote.id,
                  caveat: approvedVote.caveat,
                  type: "APPROVED",
                }}
                isRemovable
                onRemove={() =>
                  handleRemoveApprovedVote(
                    approvedVote.voteId,
                    approvedVote.isPersisted,
                    approvedVote.name
                  )
                }
              />
            ))}
          </div>
        </VotingContainer>

        <VotingContainer>
          <div className="selection-side">
            <span style={{ color: "#fe0000" }}>
              {t("forms.minutes.actions.reproved").toUpperCase()}
            </span>

            <div className="selection-container">
              <div className="inputs-container">
                <Select
                  disabled={!canSave}
                  id="participants"
                  options={reprovedSelectOptions}
                  value={reprovedSelectValue}
                  setValue={(value) => setReprovedSelectValue(value)}
                  onInputChange={(_event, newInputValue, _prop) => {
                    setReprovedInputValue(newInputValue);
                  }}
                  inputValue={reprovedInputValue}
                  label={t("name")}
                />

                <Textarea
                  name="reprovedCaveats"
                  id="reprovedCaveats"
                  disabled={!canSave}
                  value={reprovedCaveat}
                  label={t("forms.minutes.actions.caveats")}
                  onChange={(e) => setReprovedCaveat(e.target.value)}
                />
              </div>
              <div className="add-button-container">
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  onClick={handleAddReprovedVote}
                  disabled={unanimityApproval | unanimityDisapproval | !canSave}
                >
                  <Add />
                </Fab>
              </div>
            </div>

            <FormControlLabel
              fullWidth
              control={
                <Checkbox
                  value={unanimityDisapproval}
                  checked={unanimityDisapproval}
                  disabled={unanimityApproval | !canSave}
                  onChange={() => updateDisapprovalUnannimity()}
                  name="unanimityDisapproval"
                  id="unanimityDisapproval"
                  color="primary"
                />
              }
              label={t("menu.minutes.slotApproval.unanimityDisapproval")}
            />
          </div>
          <div className="voters-side">
            {reprovedVotes.map((reproved, index) => (
              <ApproverCard
                key={reproved.voteId}
                approver={{
                  memberName: reproved.name,
                  approvalId: reproved.id,
                  caveat: reproved.caveat,
                  type: "REPPROVED",
                }}
                isRemovable
                onRemove={() =>
                  handleRemoveReprovedVote(
                    reproved.voteId,
                    reproved.isPersisted,
                    reproved.name
                  )
                }
              />
            ))}
          </div>
        </VotingContainer>

        <VotingContainer>
          <div className="selection-side">
            <span style={{ color: "#465eb6" }}>
              {t("forms.minutes.actions.roudRobbinapproved").toUpperCase()}
            </span>

            <div className="selection-container">
              <div className="inputs-container">
                <Select
                  disabled={!canSave}
                  id="participants"
                  options={roudRobbinApprovedSelectOptions}
                  value={roudRobbinApprovedSelectValue}
                  setValue={(value) => setRoudRobbinApprovedSelectValue(value)}
                  onInputChange={(_event, newInputValue, _prop) => {
                    setRoudRobbinApprovedInputValue(newInputValue);
                  }}
                  inputValue={roudRobbinApprovedInputValue}
                  label={t("name")}
                />
                <KeyboardDatePicker
                  disabled={!canSave}
                  autoOk
                  disableToolbar
                  variant="inline"
                  format={getInputDateFormats(i18n.language)}
                  id="date-picker-inline"
                  fullWidth
                  label={t("meeting.meetingDate")}
                  value={roudRobbindate}
                  onChange={(date) => {
                    setRoudRobbindate(date);
                  }}
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                />

                <Textarea
                  name="roundRobinApprovedCaveats"
                  disabled={!canSave}
                  id="roundRobinApprovedCaveats"
                  value={roundRobinApprovedCaveat}
                  label={t("forms.minutes.actions.caveats")}
                  onChange={(e) => setRoundRobinApprovedCaveat(e.target.value)}
                />
              </div>
              <div className="add-button-container">
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  onClick={handleAddRoudRobbinApprovedVote}
                  disabled={unanimityApproval | unanimityDisapproval | !canSave}
                >
                  <Add />
                </Fab>
              </div>
            </div>
          </div>
          <div className="voters-side">
            {roundRobinVotes.map((roundRobinVoter, index) => {
              return (
                <ApproverCard
                  key={roundRobinVoter.voteId}
                  approver={{
                    memberName: roundRobinVoter.name,
                    approvalId: roundRobinVoter.id,
                    caveat: roundRobinVoter.caveat,
                    roundRobinDate: roundRobinVoter.formattedRoudRobbinDate,
                    type: "ROUND_ROBIN_APPROVED",
                  }}
                  isRemovable
                  onRemove={() =>
                    handleRemoveRoudRobbinApprovedVote({
                      memberToRemove: roundRobinVoter.voteId,
                      isPersisted: roundRobinVoter.isPersisted,
                      roundRobinVoterName: roundRobinVoter.name,
                    })
                  }
                />
              );
            })}
          </div>
        </VotingContainer>

        <VotingContainer>
          <div className="selection-side">
            <span style={{ color: "#FEBC00" }}>
              {t("forms.minutes.actions.caveats").toUpperCase()}
            </span>

            <div className="selection-container">
              <div className="inputs-container">
                <Textarea
                  name="caveats"
                  id="caveats"
                  value={caveat}
                  label={t("forms.minutes.actions.caveats")}
                  disabled={!canSave}
                  onChange={(e) => setCaveat(e.target.value)}
                />
              </div>
              <div className="add-button-container">
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  onClick={handleAddCaveats}
                  disabled={!canSave}
                >
                  <Add />
                </Fab>
              </div>
            </div>
          </div>
          <div className="voters-side">
            {caveats.map((vote, index) => (
              <ApproverCard
                key={vote.voteId}
                approver={{
                  approvalId: vote.id,
                  caveat: vote.caveat,
                  type: "COMMENT",
                }}
                isRemovable
                onRemove={() =>
                  handleRemoveCaveat({
                    caveatId: vote.voteId,
                    isPersisted: vote.isPersisted,
                    caveatDescription: vote.caveat,
                  })
                }
              />
            ))}
          </div>
        </VotingContainer>

        <ConcludeButtonContainer>
          <Button
            variant="contained"
            className={classes.button}
            onClick={handleSave}
            disabled={!canSave}
          >
            {t("forms.minutes.actions.saveVotes")}
          </Button>
        </ConcludeButtonContainer>
      </div>
    </>
  );
};

export default SlotApproval;
